import { SyntheticEvent, useCallback, useRef } from 'react';
import classNames from 'classnames';

import { BadgeColour, BadgeSize } from '@xemplo/badge';
import { DawnCross } from '@xemplo/icons';
import { ValueListItem } from '@xemplo/stateful-list-core';

import { useChipsOverflow } from '../../hooks/use-chips-overflow';

import * as S from './chips.styles';
import { ChipsProps } from './chips.types';

/**
 * A component that displays a list of selected chips with the ability to remove them.
 * When chips overflow the available space, it shows a count of selected options instead.
 *
 * @remarks This component is intended for internal use within the base multi-select component.
 */
export function Chips({ value, onRemove, disabled, readOnly }: ChipsProps) {
  const chipGroupRef = useRef<HTMLDivElement>(null);
  const overflow = useChipsOverflow(chipGroupRef, [value]);

  const handleRemove = useCallback(
    (e: SyntheticEvent, item: ValueListItem) => {
      e.stopPropagation();
      if (disabled) return;
      onRemove(item);
    },
    [disabled, onRemove],
  );

  if (!value || value.length === 0) return null;

  return (
    <S.ChipsWrapper>
      <S.ChipGroup
        ref={chipGroupRef}
        className={classNames({ 'hidden-chip': overflow })}
        role="group"
        aria-label="Currently selected chips"
      >
        {value.map((item) => (
          <S.Chip
            key={item.focusId}
            value={item.label}
            aria-label={`Chip: ${item.label}`}
            color={BadgeColour.LightGray}
            className={classNames({ disabled, readOnly })}
            trailingIcon={
              <DawnCross
                onClick={(e) => handleRemove(e, item)}
                aria-label={`Remove ${item.label} chip`}
              />
            }
            size={BadgeSize.Small}
            data-chip
          />
        ))}
      </S.ChipGroup>
      <S.SelectedText
        className={classNames({ 'visible-chip': overflow })}
        aria-live="polite"
        aria-atomic="true"
      >
        {value.length} options selected
      </S.SelectedText>
    </S.ChipsWrapper>
  );
}
